// Freshwater eutrophication engine — Track B6 stub.
//
// B6 will write per-HUC8 P-eq mass + spatial amplification (the ratio
// between watershed-localised and grid-mean intake fractions). Until
// then this component lights up only when a `huc8s` array is present
// in the payload, otherwise it shows the empty state.

import React from "react";
import { toSigFigs } from "../../utils/format";

interface FreshwaterEutrophEngineTableProps {
  payload: Record<string, any>;
}

interface HucRow {
  huc8_id: string;
  watershed_name: string | null;
  kg_p_eq_input: number | null;
  kg_p_eq_spatial: number | null;
  spatial_amplification: number | null;
}

function asHucRow(row: any): HucRow {
  return {
    huc8_id: String(row?.huc8_id ?? row?.huc8 ?? ""),
    watershed_name:
      typeof row?.watershed_name === "string"
        ? row.watershed_name
        : typeof row?.name === "string"
        ? row.name
        : null,
    kg_p_eq_input:
      typeof row?.kg_p_eq_input === "number" ? row.kg_p_eq_input : null,
    kg_p_eq_spatial:
      typeof row?.kg_p_eq_spatial === "number" ? row.kg_p_eq_spatial : null,
    spatial_amplification:
      typeof row?.spatial_amplification === "number" ? row.spatial_amplification : null,
  };
}

export function FreshwaterEutrophEngineTable({ payload }: FreshwaterEutrophEngineTableProps) {
  const rawHucs = Array.isArray(payload?.huc8s)
    ? payload.huc8s
    : Array.isArray(payload?.watersheds)
    ? payload.watersheds
    : null;

  if (!rawHucs || rawHucs.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No freshwater eutrophication results for this run. Track B6 (per-HUC8 spatial
        amplification) is still in flight — re-queue this project once it lands.
      </p>
    );
  }

  const rows = rawHucs.map(asHucRow).filter((r: HucRow) => r.huc8_id);
  const totalSpatial = rows.reduce(
    (acc: number, r: HucRow) => acc + (r.kg_p_eq_spatial ?? 0),
    0,
  );
  const totalInput = rows.reduce(
    (acc: number, r: HucRow) => acc + (r.kg_p_eq_input ?? 0),
    0,
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
        <div className="bg-surface-800 rounded p-3">
          <div className="text-xs text-text-muted uppercase tracking-wider">Watersheds</div>
          <div className="text-base text-text-primary mt-1">{rows.length}</div>
        </div>
        <div className="bg-surface-800 rounded p-3">
          <div className="text-xs text-text-muted uppercase tracking-wider">
            Σ kg P-eq input
          </div>
          <div className="text-base text-text-primary mt-1">{toSigFigs(totalInput)}</div>
        </div>
        <div className="bg-surface-800 rounded p-3">
          <div className="text-xs text-text-muted uppercase tracking-wider">
            Σ kg P-eq spatial
          </div>
          <div className="text-base text-text-primary mt-1">{toSigFigs(totalSpatial)}</div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-surface-600">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                HUC8
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                Watershed
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                kg P-eq input
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                kg P-eq spatial
              </th>
              <th className="px-4 py-2 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                Amplification ×
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface-700 divide-y divide-border">
            {rows.slice(0, 25).map((r: HucRow) => (
              <tr key={r.huc8_id} className="hover:bg-surface-600 transition-colors">
                <td className="px-4 py-2 text-sm font-medium text-text-primary">{r.huc8_id}</td>
                <td className="px-4 py-2 text-sm text-text-secondary">
                  {r.watershed_name ?? "—"}
                </td>
                <td className="px-4 py-2 text-right text-sm text-text-secondary">
                  {r.kg_p_eq_input === null ? "—" : toSigFigs(r.kg_p_eq_input)}
                </td>
                <td className="px-4 py-2 text-right text-sm text-text-secondary">
                  {r.kg_p_eq_spatial === null ? "—" : toSigFigs(r.kg_p_eq_spatial)}
                </td>
                <td className="px-4 py-2 text-right text-sm text-text-secondary">
                  {r.spatial_amplification === null
                    ? "—"
                    : `${toSigFigs(r.spatial_amplification)}×`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
